import type { MaybeRefOrGetter, WritableComputedRef } from 'vue'
import { computed, toValue } from 'vue'
import type { CartKey } from '~/stores/cart.store'

import { useCart } from '~/composables/useCart'

interface ProductWithCartCountLimit {
    slug?: CartKey | null
    max_cart_quantity?: number | string | null
}

interface UseCartCountModelOptions {
    min?: number
}

export const useCartCountModel = (
    product: MaybeRefOrGetter<ProductWithCartCountLimit | null | undefined>,
    options: UseCartCountModelOptions = {}
): WritableComputedRef<number> => {
    const { getItemCount, setCount } = useCart()
    const min = options.min ?? 0

    return computed({
        get: () => getItemCount(toValue(product)?.slug) || 0,
        set: (value: number | string) => {
            const currentProduct = toValue(product)
            const parsed = Number(value)
            const parsedMaxCartQuantity = Number(
                currentProduct?.max_cart_quantity ?? Number.MAX_SAFE_INTEGER
            )
            const maxCartQuantity = Number.isFinite(parsedMaxCartQuantity)
                ? parsedMaxCartQuantity
                : Number.MAX_SAFE_INTEGER

            if (!Number.isFinite(parsed)) {
                setCount(currentProduct?.slug, min)
                return
            }

            const normalized = Math.min(
                Math.max(Math.round(parsed), min),
                maxCartQuantity
            )

            setCount(currentProduct?.slug, normalized)
        },
    })
}
